import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ClientServicePricing } from '../../../data/models/client-service-pricing.model';
import { ServicePlan } from '../../../data/models/client-program.model';
import { PricingModel } from '../../../data/models/pricing-model.model';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientPricingList';

const classes = {
  container: `${PREFIX}-container`,
  panelTools: `${PREFIX}-panelTools`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  details: `${PREFIX}-details`,
  label: `${PREFIX}-label`,
  chips: `${PREFIX}-chips`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
    margin: theme.spacing(2),
  },

  [`& .${classes.panelTools}`]: {
    width: '100%',
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: 700,
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },

  [`& .${classes.chips}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface ClientPricingListProps {
  clientServicePricings: ClientServicePricing[];
  servicePlans: ServicePlan[];
  pricingModels: PricingModel[];
  onEdit: (evt: any, clientServicePricing: ClientServicePricing) => void;
  onActivate: (evt: any, clientServicePricing: ClientServicePricing) => void;
  onDeactivate: (evt: any, clientServicePricing: ClientServicePricing) => void;
  onDelete: (evt: any, clientServicePricing: ClientServicePricing) => void;
}

export default function ClientPricingList({
  clientServicePricings,
  servicePlans,
  pricingModels,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
}: ClientPricingListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const chargeTypes = useAppSelector(lookupSlice.selectChargeTypes);
  const transportServices = useAppSelector(
    accountingSlice.selectTransportationServices,
  );

  const handleActiveChange = (
    evt: any,
    clientServicePricing: ClientServicePricing,
  ) => {
    if (evt.target.checked) {
      onActivate(evt, clientServicePricing);
    } else {
      onDeactivate(evt, clientServicePricing);
    }
  };

  const getServicePlanName = (id: string) => {
    const sp = servicePlans.find((s) => s.servicePlanUUID === id);
    return sp ? sp.name : '';
  };

  const getTransportServiceName = (id: string) => {
    const ts = transportServices.find(
      (t) => t.transportationServiceUUID === id,
    );
    return ts ? ts.name : '';
  };

  const getPricingModelName = (id: string) => {
    const pm = pricingModels.find((p) => p.pricingModelUUID === id);
    return pm ? pm.name : '';
  };

  const getChargeTypeName = (id: string) => {
    const ct = chargeTypes.find((c) => c.chargeTypeBC === id);
    return ct ? ct.chargeTypeName : '';
  };

  return (
    <StyledBox className={classes.container}>
      {clientServicePricings.map((servicePricing) => {
        const isActive = servicePricing.state === 'active';

        return (
          <Accordion key={servicePricing.clientServicePricingUUID}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.panelTools}>
                <Box className={classes.summary}>
                  <Typography className={classes.panelTitle}>
                    {servicePricing.name}
                  </Typography>
                  <Box>
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.editServicePricing')}>
                        <span>
                          <IconButton
                            data-testid="edit-contact"
                            disabled={isActive}
                            onClick={(evt) => onEdit(evt, servicePricing)}
                            color="primary"
                            size="large">
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.deleteServicePricing')}>
                        <span>
                          <IconButton
                            data-testid="delete-pricing-model"
                            disabled={isActive}
                            onClick={(evt) => onDelete(evt, servicePricing)}
                            color="error"
                            size="large">
                            <DeleteForeverIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip
                        title={
                          servicePricing.state === 'active'
                            ? t('common.active')
                            : t('common.inactive')
                        }>
                        <Switch
                          checked={servicePricing.state === 'active'}
                          onChange={(evt) =>
                            handleActiveChange(evt, servicePricing)
                          }
                          onClick={(evt) => evt.stopPropagation()}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    {/* <Grid item xs={12} className={classes.panelTitle}> */}
                    <div className={classes.panelTitle}>
                      {t('client.servicePlans')}
                    </div>
                    {/* </Grid>
                    <Grid item xs={12}> */}
                    {servicePricing.servicePlanUUIDs.map((sp) => {
                      return (
                        // <Grid item xs={12} key={sp}>
                        <div key={sp}>{getServicePlanName(sp)}</div>
                        // </Grid>
                      );
                    })}
                    {/* </Grid> */}
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} className={classes.panelTitle}>
                    {t('client.transportationServices')}
                  </Grid>
                  <Grid item xs={12} className={classes.chips}>
                    {servicePricing.transportationServiceUUIDs.map((ts) => {
                      return (
                        <Chip
                          color="primary"
                          label={getTransportServiceName(ts)}
                          key={ts}
                        />
                      );
                    })}
                  </Grid>
                  <Grid item xs={12} className={classes.panelTitle}>
                    {t('client.pricingModel')}
                  </Grid>
                  <Grid item xs={12}>
                    {getPricingModelName(
                      servicePricing.pricingModel.pricingModelUUID,
                    )}
                  </Grid>
                  <Grid item container spacing={2}>
                    <Grid item xs={12} className={classes.panelTitle}>
                      {t('client.charges')}
                    </Grid>
                    {servicePricing.pricingModel.charges.map((ch) => {
                      return (
                        <Grid item container xs={12} key={ch.chargeTypeBC}>
                          <Grid item xs={3}>
                            {getChargeTypeName(ch.chargeTypeBC)}
                          </Grid>
                          <Grid item container xs={9}>
                            {ch.functionParameters.map((fp, idx) => {
                              return (
                                <Grid item xs={12} key={idx}>
                                  {fp.name}: {fp.value}
                                </Grid>
                              );
                            })}

                            {ch.cptCodeBC && (
                              <Grid item xs={12}>
                                {t('client.cptCode')}: {ch.cptCodeBC}
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </StyledBox>
  );
}
